const API_BASE_URL =
  process.env.REACT_APP_API_URL || 'https://lulliconazole.onrender.com/api';

export const API_ENDPOINTS = {
  // Authentication
  SEND_OTP: `${API_BASE_URL}/auth/send-otp`,
  VERIFY_OTP: `${API_BASE_URL}/auth/verify-otp`,
  RESEND_OTP: `${API_BASE_URL}/auth/resend-otp`,
  CHECK_EMAIL: `${API_BASE_URL}/auth/check-email`,
  LOGOUT: `${API_BASE_URL}/auth/logout`,
  PROFILE: `${API_BASE_URL}/auth/profile`,
  LOGIN_HISTORY: `${API_BASE_URL}/auth/login-history`,

  // User Management
  USERS_LIST: `${API_BASE_URL}/users/list`,
  USER_BY_ID: (id) => `${API_BASE_URL}/users/${id}`,
  USER_CREATE: `${API_BASE_URL}/users/create`,
  USER_UPDATE: (id) => `${API_BASE_URL}/users/${id}`,
  USER_DELETE: (id) => `${API_BASE_URL}/users/${id}`,
  USER_HISTORY: (id) => `${API_BASE_URL}/users/${id}/history`,
  BULK_IMPORT: `${API_BASE_URL}/users/bulk-import`,
  USERS_BY_ROLE: (roleId) => `${API_BASE_URL}/users/by-role/${roleId}`,

  // Role Management
  ROLES_LIST: `${API_BASE_URL}/roles/list`,
  ROLE_BY_ID: (id) => `${API_BASE_URL}/roles/${id}`,
  ROLE_CREATE: `${API_BASE_URL}/roles/create`,
  ROLE_UPDATE: (id) => `${API_BASE_URL}/roles/${id}`,
  ROLE_DELETE: (id) => `${API_BASE_URL}/roles/${id}`,
  ROLE_USERS: (id) => `${API_BASE_URL}/roles/${id}/users`,
  ROLE_HISTORY: (id) => `${API_BASE_URL}/roles/${id}/history`,
  ROLES_DROPDOWN: `${API_BASE_URL}/roles/dropdown/list`,

  // Survey Management (NEW)
  SURVEY_GET_STUDY: (userId, studyId) =>
    `${API_BASE_URL}/survey/study/${userId}/${studyId}`,
  SURVEY_SUBMIT: `${API_BASE_URL}/survey/submit-survey`,
  SURVEY_SAVE_DRAFT: `${API_BASE_URL}/survey/save-draft`,
  SURVEY_USER_RESPONSES: (userId, studyId) =>
    `${API_BASE_URL}/survey/user-responses/${userId}/${studyId}`,
  SURVEY_HEALTH: `${API_BASE_URL}/survey/health`,
};

// Get auth token from localStorage
export const getAuthToken = () => {
  return localStorage.getItem('token');
};

// Set auth token in localStorage
export const setAuthToken = (token) => {
  if (token) {
    localStorage.setItem('token', token);
  } else {
    localStorage.removeItem('token');
  }
};

// Remove auth token
export const removeAuthToken = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
};

// Generic API call function
export const apiCall = async (url, options = {}) => {
  const token = getAuthToken();

  const config = {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  };

  // Add authorization header if token exists
  if (token && !options.skipAuth) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }

  try {
    console.log(`  API Call: ${options.method || 'GET'} ${url}`);
    console.log(
      '  Request:',
      options.body ? JSON.parse(options.body) : 'No body'
    );

    const response = await fetch(url, config);
    const data = await response.json();

    console.log('  Response Status:', response.status);
    console.log('  Response Data:', data);

    if (!response.ok) {
      throw new Error(data.message || `HTTP error! status: ${response.status}`);
    }

    if (!data.success) {
      throw new Error(data.message || 'API call failed');
    }

    return data;
  } catch (error) {
    console.error(' API Error:', error);

    // Handle specific errors
    if (error.message.includes('401') || error.message.includes('token')) {
      removeAuthToken();
      window.location.href = '/login';
    }

    throw error;
  }
};

// Specific API functions for easier use

// Authentication APIs
export const sendOTP = (email_address) => {
  return apiCall(API_ENDPOINTS.SEND_OTP, {
    method: 'POST',
    body: JSON.stringify({ email_address }),
    skipAuth: true, // No auth needed for login
  });
};

export const verifyOTP = (email_address, otp) => {
  return apiCall(API_ENDPOINTS.VERIFY_OTP, {
    method: 'POST',
    body: JSON.stringify({ email_address, otp }),
    skipAuth: true, // No auth needed for login
  });
};

export const resendOTP = (email_address) => {
  return apiCall(API_ENDPOINTS.RESEND_OTP, {
    method: 'POST',
    body: JSON.stringify({ email_address }),
    skipAuth: true,
  });
};

export const checkEmail = (email_address) => {
  return apiCall(API_ENDPOINTS.CHECK_EMAIL, {
    method: 'POST',
    body: JSON.stringify({ email_address }),
    skipAuth: true,
  });
};

export const logout = () => {
  return apiCall(API_ENDPOINTS.LOGOUT, {
    method: 'POST',
  });
};

export const getProfile = () => {
  return apiCall(API_ENDPOINTS.PROFILE);
};

export const updateProfile = (data) => {
  return apiCall(API_ENDPOINTS.PROFILE, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

// User Management APIs
export const getUsersList = (params) => {
  return apiCall(API_ENDPOINTS.USERS_LIST, {
    method: 'POST',
    body: JSON.stringify(params),
  });
};

export const getUserById = (id) => {
  return apiCall(API_ENDPOINTS.USER_BY_ID(id));
};

export const createUser = (userData) => {
  return apiCall(API_ENDPOINTS.USER_CREATE, {
    method: 'POST',
    body: JSON.stringify(userData),
  });
};

export const updateUser = (id, userData) => {
  return apiCall(API_ENDPOINTS.USER_UPDATE(id), {
    method: 'PUT',
    body: JSON.stringify(userData),
  });
};

export const deleteUser = (id, deleted_by) => {
  return apiCall(API_ENDPOINTS.USER_DELETE(id), {
    method: 'DELETE',
    body: JSON.stringify({ deleted_by }),
  });
};

export const getUserHistory = (id, page = 1, limit = 20) => {
  return apiCall(
    `${API_ENDPOINTS.USER_HISTORY(id)}?page=${page}&limit=${limit}`
  );
};

// Role Management APIs
export const getRolesList = (params) => {
  return apiCall(API_ENDPOINTS.ROLES_LIST, {
    method: 'POST',
    body: JSON.stringify(params),
  });
};

export const getRolesDropdown = () => {
  return apiCall(API_ENDPOINTS.ROLES_DROPDOWN);
};

export const getRoleById = (id) => {
  return apiCall(API_ENDPOINTS.ROLE_BY_ID(id));
};

export const createRole = (roleData) => {
  return apiCall(API_ENDPOINTS.ROLE_CREATE, {
    method: 'POST',
    body: JSON.stringify(roleData),
  });
};

export const updateRole = (id, roleData) => {
  return apiCall(API_ENDPOINTS.ROLE_UPDATE(id), {
    method: 'PUT',
    body: JSON.stringify(roleData),
  });
};

export const deleteRole = (id, deleted_by) => {
  return apiCall(API_ENDPOINTS.ROLE_DELETE(id), {
    method: 'DELETE',
    body: JSON.stringify({ deleted_by }),
  });
};

/**
 * Get study data for a user
 * @param {number} userId - User ID
 * @param {number} studyId - Study ID
 * @returns {Promise} Study data
 */
export const getStudy = (userId, studyId) => {
  return apiCall(API_ENDPOINTS.SURVEY_GET_STUDY(userId, studyId), {
    method: 'GET',
    skipAuth: false, // Set to false if you want authentication
  });
};

/**
 * Submit survey responses
 * @param {number} userId - User ID
 * @param {number} studyId - Study ID
 * @param {object} responseData - Survey response data
 * @returns {Promise} Submission result
 */
export const submitSurvey = (userId, studyId, responseData) => {
  return apiCall(API_ENDPOINTS.SURVEY_SUBMIT, {
    method: 'POST',
    body: JSON.stringify({
      userId,
      studyId,
      responseData,
    }),
    skipAuth: false, // Set to false if you want authentication
  });
};

/**
 * Save survey as draft
 * @param {number} userId - User ID
 * @param {number} studyId - Study ID
 * @param {object} responseData - Partial survey data
 * @returns {Promise} Save result
 */
export const saveSurveyDraft = (userId, studyId, responseData) => {
  return apiCall(API_ENDPOINTS.SURVEY_SAVE_DRAFT, {
    method: 'POST',
    body: JSON.stringify({
      userId,
      studyId,
      responseData,
    }),
    skipAuth: false, // Set to false if you want authentication
  });
};

/**
 * Get user's previous survey responses
 * @param {number} userId - User ID
 * @param {number} studyId - Study ID
 * @returns {Promise} User responses
 */
export const getUserSurveyResponses = (userId, studyId) => {
  return apiCall(API_ENDPOINTS.SURVEY_USER_RESPONSES(userId, studyId), {
    method: 'GET',
    skipAuth: true, // Set to false if you want authentication
  });
};

/**
 * Health check for survey API
 * @returns {Promise} Health status
 */
export const surveyHealthCheck = () => {
  return apiCall(API_ENDPOINTS.SURVEY_HEALTH, {
    method: 'GET',
    skipAuth: true,
  });
};

export default {
  API_ENDPOINTS,
  apiCall,
  getAuthToken,
  setAuthToken,
  removeAuthToken,
  // Auth
  sendOTP,
  verifyOTP,
  resendOTP,
  checkEmail,
  logout,
  getProfile,
  updateProfile,
  // Users
  getUsersList,
  getUserById,
  createUser,
  updateUser,
  deleteUser,
  getUserHistory,
  // Roles
  getRolesList,
  getRolesDropdown,
  getRoleById,
  createRole,
  updateRole,
  deleteRole,
  // Survey (NEW)
  getStudy,
  submitSurvey,
  saveSurveyDraft,
  getUserSurveyResponses,
  surveyHealthCheck,
};
